import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Error as ErrorIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  Menu as MenuIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FindInPage as FindInPageIcon
} from '@material-ui/icons';
import { Button } from './Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './Card';
import { processQuestion, getQueryHistory, getLocalQueryHistory, deleteQueryHistory, deleteLocalQueryHistoryItem, QueryHistory, ProcessedResult } from '../../services';

const useStyles = makeStyles((theme) => ({
  // Layout and History sidebar styles
  container: {
    display: 'flex',
    width: '100%',
    height: '100%',
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  sidebar: {
    width: '280px',
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s ease',
    height: '100%',
    maxHeight: '100%',
    backgroundColor: '#f9f9f9',
    boxShadow: '1px 0 5px rgba(0, 0, 0, 0.05)',
    '@media (max-width: 768px)': {
      width: '100%',
      maxHeight: '250px',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  sidebarCollapsed: {
    width: '0px',
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#f0f0f0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  searchInputContainer: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: '56px', // Height of the header
    backgroundColor: '#f9f9f9',
    zIndex: 9,
  },
  historySearchInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: '4px',
      fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
    },
    '& .MuiInputAdornment-root': {
      marginRight: '4px',
    },
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  sidebarContent: {
    flex: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: '3px',
    },
  },
  historyItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
    transition: 'all 0.2s ease',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
  },
  historyItemContent: {
    flex: 1,
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateX(4px)',
    },
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
  },
  historyQuery: {
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  historyDate: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  historyFilters: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'relative',
    '@media (max-width: 768px)': {
      height: 'calc(100% - 250px)',
    },
  },
  // Toggle button moved to header
  toggleButtonContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  // Header area for when sidebar is collapsed
  collapsedHeader: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 100,
    backgroundColor: '#f0f0f0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderRadius: '0 0 4px 0',
    boxShadow: '1px 0 3px rgba(0, 0, 0, 0.1)',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  thinkingIcon: {
    animation: '$pulse 1.5s infinite ease-in-out',
    color: theme.palette.primary.main,
  },
  thinkingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'inline-block',
    marginRight: '4px',
    animation: '$pulse 1.5s infinite ease-in-out',
  },
  thinkingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: '100px',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 0.6,
      transform: 'scale(0.9)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
    '100%': {
      opacity: 0.6,
      transform: 'scale(0.9)',
    },
  },
  sendButton: {
    borderRadius: '24px',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
    },
    minWidth: '90px',
  },
  noHistory: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  refreshButton: {
    marginLeft: 'auto',
  },
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 10,
    padding: theme.spacing(2, 0),
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 4px 6px -6px rgba(0, 0, 0, 0.1)',
  },
  textFieldContainer: {
    position: 'relative',
  },
  textField: {
    width: '100%',
  },
  hint: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  errorContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
  },
  errorContent: {
    marginLeft: theme.spacing(1),
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  resultsContainer: {
    marginTop: theme.spacing(3),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: theme.shape.borderRadius,
  },
  analysisTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    width: '100%',
  },
  filterChip: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  responseContainer: {
    padding: theme.spacing(4),
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    flex: 1,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    maxWidth: '700px',
    margin: '0 auto',
    width: '100%',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '6px',
      background: 'linear-gradient(rgba(0, 0, 0, 0.05), transparent)',
      zIndex: 1,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
    },
  },
  markdownContainer: {
    padding: theme.spacing(1, 0),
  },
  // New list-based bullet point styles
  listBulletPoint: {
    padding: theme.spacing(0.5, 0),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
  },
  textLine: {
    padding: theme.spacing(0.5, 0),
    marginBottom: theme.spacing(0.5),
  },
  headingLine: {
    padding: theme.spacing(1, 0, 0.5, 0),
    marginBottom: theme.spacing(0.5),
  },
  bulletIcon: {
    minWidth: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginTop: '4px',
  },
  responseWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  // Original bullet point styles - kept for backward compatibility
  bulletPoint: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'flex-start',
  },
  bullet: {
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: '4px',
  },
  recommendationSection: {
    backgroundColor: 'rgba(76, 175, 80, 0.08)',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.success.main}`,
    marginTop: theme.spacing(2),
  },
  recommendationTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.success.dark,
  },
  recommendationContent: {
    marginTop: theme.spacing(1),
  },
  // Recommendation bullet styles - kept for backward compatibility
  recommendationBullet: {
    color: theme.palette.success.main,
    flexShrink: 0,
    marginTop: '4px',
  },
}));

// Using ProcessedResult from services

export interface VectorSearchProps {
  selectedQuestion?: string;
  // Props for existing application filters
  facilityNames?: string[];
  classificationNames?: string[];
  classificationTypes?: string[];
  startDate?: string;
  endDate?: string;
}



export const VectorSearch: React.FC<VectorSearchProps> = ({
  selectedQuestion = '',
  facilityNames: propFacilityNames = [],
  classificationNames: propClassificationNames = [],
  classificationTypes: propClassificationTypes = [],
  startDate: propStartDate = '',
  endDate: propEndDate = ''
}) => {
  const classes = useStyles();
  const [query, setQuery] = useState(selectedQuestion);
  const [results, setResults] = useState<ProcessedResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  // Thread management - backend controls thread lifecycle
  const [threadId, setThreadId] = useState<string>('');

  // Local state for filters that can be updated from history
  const [localFacilityNames, setLocalFacilityNames] = useState<string[]>(propFacilityNames);
  const [localClassificationNames, setLocalClassificationNames] = useState<string[]>(propClassificationNames);
  const [localClassificationTypes, setLocalClassificationTypes] = useState<string[]>(propClassificationTypes);
  const [localStartDate, setLocalStartDate] = useState<string>(propStartDate);
  const [localEndDate, setLocalEndDate] = useState<string>(propEndDate);
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<QueryHistory | null>(null);

  // History sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [queryHistory, setQueryHistory] = useState<QueryHistory[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<QueryHistory[]>([]);
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    setMounted(true);
    loadQueryHistory();

    // Thread ID will be managed by backend - start with empty string
  }, []);
  
  // Load query history from server and localStorage
  const loadQueryHistory = async () => {
    setLoadingHistory(true);
    try {
      // Try to get history from server first
      const serverHistory = await getQueryHistory();
      setQueryHistory(serverHistory);
      setFilteredHistory(serverHistory);
    } catch (error) {
      console.error('Failed to load history from server, falling back to localStorage:', error);
      // Fall back to localStorage
      const localHistory = getLocalQueryHistory();
      setQueryHistory(localHistory);
      setFilteredHistory(localHistory);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Filter history based on search term
  const handleHistorySearch = (searchTerm: string) => {
    setHistorySearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      // If search term is empty, show all history items
      setFilteredHistory(queryHistory);
      return;
    }
    
    // Filter history items that contain the search term (case insensitive)
    const filtered = queryHistory.filter(item => 
      item.query.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredHistory(filtered);
  };

  // Open the delete confirmation dialog
  const openDeleteDialog = (event: React.MouseEvent, historyItem: QueryHistory) => {
    // Stop the event from propagating to the parent (which would trigger the history item click)
    event.stopPropagation();
    setItemToDelete(historyItem);
    setDeleteDialogOpen(true);
  };

  // Close the delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Handle deleting a history item after confirmation
  const handleDeleteHistoryItem = async () => {
    if (!itemToDelete || !itemToDelete.id) {
      console.error('Cannot delete history item without an ID');
      closeDeleteDialog();
      return;
    }
    
    try {
      // Delete from server first
      await deleteQueryHistory(itemToDelete.id);
      
      // Update the local state to remove the deleted item
      const updatedHistory = queryHistory.filter(item => item.id !== itemToDelete.id);
      setQueryHistory(updatedHistory);
      
      // Also update the filtered history
      const updatedFilteredHistory = filteredHistory.filter(item => item.id !== itemToDelete.id);
      setFilteredHistory(updatedFilteredHistory);
      
      // If the current results are from this history item, clear them
      if (results && results.searchQuery === itemToDelete.query) {
        setResults(null);
        setQuery('');
        setIsReadOnly(false);
      }
      
      console.log(`Successfully deleted history item ${itemToDelete.id}`);
    } catch (error) {
      console.error(`Failed to delete history item ${itemToDelete.id}:`, error);
      // Even if server deletion fails, try to delete from localStorage
      deleteLocalQueryHistoryItem(itemToDelete.id);
      
      // Update the local state anyway (for better UX)
      const updatedHistory = queryHistory.filter(item => item.id !== itemToDelete.id);
      setQueryHistory(updatedHistory);
      setFilteredHistory(filteredHistory.filter(item => item.id !== itemToDelete.id));
    } finally {
      closeDeleteDialog();
    }
  };

  // Handle clicking on a history item
  const handleHistoryItemClick = (historyItem: QueryHistory) => {
    console.log('Clicked history item:', historyItem);
  
    // Set the query text
    setQuery(historyItem.query);
    
    // Set the search box to read-only mode
    setIsReadOnly(true);
    
    // Clear any previous errors
    setError(null);
    setIsLoading(false);
  
    try {
      // Get the raw filters
      let rawFilters = historyItem.filters;

      // Parse filters if they're a string
      if (typeof rawFilters === 'string') {
        try {
          rawFilters = JSON.parse(rawFilters);
        } catch (e) {
          console.error('Error parsing filter string:', e);
        }
      }

      console.log('Parsed filters object:', rawFilters);
      console.log('Property names in raw filters:', Object.keys(rawFilters));

      // Create variables to hold our extracted values
      let extractedFacilityNames: string[] = [];
      let extractedClassificationNames: string[] = [];
      let extractedClassificationTypes: string[] = [];
      let extractedStartDate = '';
      let extractedEndDate = '';

      // Iterate through all properties in the raw filters object
      // This approach finds the data regardless of property naming
      for (const key in rawFilters) {
        const lowerKey = key.toLowerCase();

        // Log each key and its value for debugging
        console.log(`Checking key: ${key}, Value:`, rawFilters[key]);

        if (lowerKey.includes('facility')) {
          // Found facility names
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedFacilityNames = [...value];
          }
        } else if (lowerKey.includes('classification') && lowerKey.includes('name')) {
          // Found classification names
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedClassificationNames = [...value];
          }
        } else if (lowerKey.includes('classification') && lowerKey.includes('type')) {
          // Found classification types
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedClassificationTypes = [...value];
          }
        } else if (lowerKey.includes('date') || lowerKey.includes('range')) {
          // Found date range
          const value = rawFilters[key];
          if (typeof value === 'object' && value !== null) {
            // Check for various date properties
            for (const dateKey in value) {
              const lowerDateKey = dateKey.toLowerCase();
              if (lowerDateKey.includes('start')) {
                extractedStartDate = value[dateKey] || '';
              } else if (lowerDateKey.includes('end')) {
                extractedEndDate = value[dateKey] || '';
              }
            }
          }
        }
      }

      // Log what we extracted
      console.log('Extracted values:', {
        facilityNames: extractedFacilityNames,
        classificationNames: extractedClassificationNames,
        classificationTypes: extractedClassificationTypes,
        startDate: extractedStartDate,
        endDate: extractedEndDate
      });

      // Set the state variables with our extracted values
      setLocalFacilityNames(extractedFacilityNames);
      setLocalClassificationNames(extractedClassificationNames);
      setLocalClassificationTypes(extractedClassificationTypes);
      setLocalStartDate(extractedStartDate);
      setLocalEndDate(extractedEndDate);
    } catch (error) {
      console.error('Error processing history item filters:', error);
    }

    console.log('Loading history item:', historyItem.query);
    
    // Set the results
    if (historyItem.result) {
      // Process the result...
      let responseText = '';
      
      if (typeof historyItem.result === 'string') {
        try {
          const parsed = JSON.parse(historyItem.result);
          responseText = parsed.response || '';
        } catch (e) {
          responseText = historyItem.result;
        }
      } else if (typeof historyItem.result === 'object') {
        if (historyItem.result.response) {
          responseText = typeof historyItem.result.response === 'string'
            ? historyItem.result.response
            : JSON.stringify(historyItem.result.response);
        }
      }
      
      // Create a properly structured ProcessedResult
      const processedResult: ProcessedResult = {
        searchQuery: historyItem.query,
        response: responseText
      };
      
      // Set the results state
      setResults(processedResult);
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateString;
    }
  };
  
  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Effect to update query when selectedQuestion changes
  useEffect(() => {
    if (selectedQuestion) {
      setQuery(selectedQuestion);
    }
  }, [selectedQuestion]);

  // Debug effect to log filter state changes
  useEffect(() => {
    console.log('Filter state updated:', {
      facilityNames: localFacilityNames,
      classificationNames: localClassificationNames,
      classificationTypes: localClassificationTypes,
      startDate: localStartDate,
      endDate: localEndDate
    });
  }, [localFacilityNames, localClassificationNames, localClassificationTypes, localStartDate, localEndDate]);
  
  if (!mounted) {
    return null;
  }



  const handleSearch = async () => {
    if (!query.trim()) return;
    
    console.group('Processing question');
    console.log('Query:', query);
    console.log('Filters:', {
      facilityNames: localFacilityNames,
      classificationNames: localClassificationNames,
      classificationTypes: localClassificationTypes,
      startDate: localStartDate,
      endDate: localEndDate
    });
    console.log('Current Thread ID:', threadId);

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const result = await processQuestion(
        query,
        threadId,
        localFacilityNames,
        localClassificationNames,
        localClassificationTypes,
        localStartDate,
        localEndDate
      );

      console.log('Received result:', result);
      setResults(result);

      // Update thread ID if backend returned a new one
      if (result.threadId) {
        console.log('Backend returned new thread ID:', result.threadId);
        setThreadId(result.threadId);
      }

      // Refresh history after new query
      loadQueryHistory();
    } catch (err) {
      console.error('Error processing question:', err);
      
      // Handle specific error types
      if (err instanceof Error && err.message.includes('Failed to fetch')) {
        setError(
          'Network error: Failed to connect to the Azure OpenAI service. ' +
          'Please check your internet connection and ensure the Azure OpenAI endpoint is accessible. ' +
          'See browser console for detailed logs.'
        );
      } else if (err instanceof Error && err.message.includes('Azure OpenAI endpoint or API key is not configured')) {
        setError(
          'Configuration error: Azure OpenAI is not properly configured. ' +
          'Please check that REACT_APP_AZURE_ENDPOINT and REACT_APP_AZURE_API_KEY are set in your .env.local file.'
        );
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred while processing your question');
      }
    } finally {
      console.groupEnd();
      setIsLoading(false);
    }
  };

  return (
    <div ref={componentRef} className={classes.root}>
      <Card className={classes.card}>
        <CardHeader>
          <CardTitle className={classes.title}>Incident Intelligence</CardTitle>
          <CardDescription>
            Ask questions about incident data, trends, classifications, and gain actionable insights.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className={classes.container}>
            {/* History Sidebar - only show when there's history */}
            {queryHistory.length > 0 && (
              <div className={`${classes.sidebar} ${!sidebarOpen ? classes.sidebarCollapsed : ''}`}>
              <div className={classes.sidebarHeader}>
                <Typography className={classes.sidebarTitle}>
                  <HistoryIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Query History
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    size="small" 
                    className={classes.refreshButton}
                    onClick={loadQueryHistory}
                    disabled={loadingHistory}
                    style={{ marginRight: 4 }}
                    title="Refresh history"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small"
                    onClick={toggleSidebar}
                    title={sidebarOpen ? "Hide history" : "Show history"}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              
              {/* Search input for history */}
              <div className={classes.searchInputContainer}>
                <TextField
                  className={classes.historySearchInput}
                  placeholder="Search history..."
                  variant="outlined"
                  size="small"
                  value={historySearchTerm}
                  onChange={(e) => handleHistorySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon fontSize="small" style={{ color: '#757575', marginRight: 8 }} />
                    ),
                    endAdornment: historySearchTerm ? (
                      <IconButton
                        size="small"
                        onClick={() => handleHistorySearch('')}
                        style={{ padding: 4 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }}
                />
              </div>
              
              <div className={classes.sidebarContent}>
                {loadingHistory ? (
                  <div style={{ padding: 16, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </div>
                ) : queryHistory.length === 0 ? (
                  <div className={classes.noHistory}>
                    <Typography variant="body2">No query history found</Typography>
                  </div>
                ) : filteredHistory.length === 0 && historySearchTerm ? (
                  <div className={classes.noHistory}>
                    <Typography variant="body2">No matching queries found</Typography>
                  </div>
                ) : (
                  <List>
                    {filteredHistory.map((item) => (
                      <Tooltip
                        key={item.id}
                        title={
                          <span style={{ display: 'block', maxWidth: '300px' }}>
                            <Typography variant="body2">{item.query}</Typography>
                          </span>
                        }
                        placement="top"
                        arrow
                      >
                        <ListItem 
                          className={classes.historyItem}
                          onClick={() => handleHistoryItemClick(item)}
                        >
                          <ListItemText
                            primary={
                              <Typography className={classes.historyQuery} noWrap>
                                {item.query}
                              </Typography>
                            }
                            secondary={
                              <>
                                <div className={classes.historyDate}>
                                  {formatDate(item.createdDate)}
                                </div>
                              </>
                            }
                          />
                        </ListItem>
                      </Tooltip>
                    ))}
                  </List>
                )}
              </div>
            </div>
            )}
            
            {/* Header with toggle button when sidebar is collapsed */}
            {queryHistory.length > 0 && !sidebarOpen && (
              <div className={classes.collapsedHeader}>
                <IconButton 
                  onClick={toggleSidebar}
                  size="small"
                  title="Show history"
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" style={{ marginLeft: 4, fontWeight: 500 }}>
                  History
                </Typography>
              </div>
            )}
            
            {/* Main Content */}
            <div className={classes.mainContent}>
              <div className={classes.searchContainer}>
                <div className={classes.textFieldContainer}>
                  <TextField
                    value={query}
                    onChange={(e) => !isReadOnly && setQuery(e.target.value)}
                    placeholder="What types of incidents are trending upward?"
                    variant="outlined"
                    multiline
                    minRows={2}
                    className={classes.textField}
                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && !isReadOnly && handleSearch()}
                    InputProps={{
                      readOnly: isReadOnly,
                      style: isReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}
                    }}
                  />
                  <div className={classes.hint}>
                    {isReadOnly ? 'This is a historical query' : 'Press Ctrl+Enter to search'}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  {isReadOnly && (
                    <>
                      <Button
                        onClick={() => {
                          setIsReadOnly(false);
                          setQuery('');
                          setResults(null);
                          // Preserve parent application filters by resetting to prop values
                          setLocalFacilityNames(propFacilityNames);
                          setLocalClassificationNames(propClassificationNames);
                          setLocalClassificationTypes(propClassificationTypes);
                          setLocalStartDate(propStartDate);
                          setLocalEndDate(propEndDate);
                          // Reset thread ID - backend will provide new thread ID for new conversation
                          setThreadId('');
                        }}
                        variant="secondary"
                      >
                        New Search
                      </Button>
                      {/* Using Material-UI Button directly for the delete button since our custom Button doesn't support color */}
                      <MuiButton
                        onClick={(event) => {
                          // Find the current history item based on the query
                          const currentItem = queryHistory.find(item => item.query === query);
                          if (currentItem) {
                            openDeleteDialog(event, currentItem);
                          }
                        }}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        startIcon={<DeleteIcon fontSize="small" />}
                        disabled={isLoading}
                        style={{ marginLeft: '8px' }}
                      >
                        Delete
                      </MuiButton>
                    </>
                  )}
                  <Button 
                    onClick={handleSearch} 
                    disabled={!query.trim() || isLoading || isReadOnly}
                    endIcon={!isLoading && <FindInPageIcon fontSize="small" />}
                    className={classes.sendButton}
                  >
                    {isLoading ? (
                      <div className={classes.thinkingContainer}>
                        <Typography variant="body2" style={{ fontWeight: 500, color: '#ffffff' }}>Thinking</Typography>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                          <span className={classes.thinkingDot} style={{ animationDelay: '0s' }}></span>
                          <span className={classes.thinkingDot} style={{ animationDelay: '0.2s' }}></span>
                          <span className={classes.thinkingDot} style={{ animationDelay: '0.4s' }}></span>
                        </div>
                      </div>
                    ) : 'Ask'}
                  </Button>
                </div>
              </div>
              
                {error && (
                <div className={classes.errorContainer}>
                  <ErrorIcon className={classes.errorIcon} />
                  <div className={classes.errorContent}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      Error: {error.includes(':') ? error.split(':')[0] + ':' : 'Request Failed'}
                    </Typography>
                    <Typography variant="body2">
                      {error.includes(':') ? error.split(':').slice(1).join(':').trim() : error}
                    </Typography>
                    {error.includes('Failed to fetch') && (
                      <Typography variant="body2" style={{ marginTop: '8px' }}>
                        Tip: Open the browser console (F12) for detailed error information.
                      </Typography>
                    )}
                  </div>
                </div>
              )}

              {results && (
                <div className={classes.resultsContainer}>
                  {/* Response Section */}
                  <div>
                    <Typography variant="h6" className={classes.analysisTitle}>Summary</Typography>

                    {/* Filter section with improved visibility */}
                    <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 8, fontWeight: 500 }}>
                      Applied Filters:
                    </Typography>
                    <div className={classes.filterChips}>
                      {/* Facility Names Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localFacilityNames.length > 0 ? 1 : 0.7,
                        backgroundColor: localFacilityNames.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localFacilityNames.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localFacilityNames.length > 0 ? '#1976d2' : '#757575'
                        }} />
                        <strong>Facilities:</strong>&nbsp;
                        {localFacilityNames.length > 0 ? localFacilityNames.join(', ') : 'All Facilities'}
                      </div>

                      {/* Classification Names Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localClassificationNames.length > 0 ? 1 : 0.7,
                        backgroundColor: localClassificationNames.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localClassificationNames.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localClassificationNames.length > 0 ? '#1976d2' : '#757575'
                        }} />
                        <strong>Classifications:</strong>&nbsp;
                        {localClassificationNames.length > 0 ? localClassificationNames.join(', ') : 'All Classifications'}
                      </div>

                      {/* Classification Types Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localClassificationTypes.length > 0 ? 1 : 0.7,
                        backgroundColor: localClassificationTypes.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localClassificationTypes.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localClassificationTypes.length > 0 ? '#1976d2' : '#757575'
                        }} />
                        <strong>Types:</strong>&nbsp;
                        {localClassificationTypes.length > 0 ? localClassificationTypes.join(', ') : 'All Types'}
                      </div>

                      {/* Date Range Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: (localStartDate && localEndDate) ? 1 : 0.7,
                        backgroundColor: (localStartDate && localEndDate) ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: (localStartDate && localEndDate) ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: (localStartDate && localEndDate) ? '#1976d2' : '#757575'
                        }} />
                        <strong>Date Range:</strong>&nbsp;
                        {(localStartDate && localEndDate) ?
                          `${new Date(localStartDate).toLocaleDateString()} - ${new Date(localEndDate).toLocaleDateString()}` :
                          'All Dates'}
                      </div>
                    </div>
                    
                    <Paper className={classes.responseContainer} elevation={2}>
                    {/* Render response as Markdown with special styling for recommendations */}
                    {results.response && (
                      <div className={classes.markdownContainer}>
                        {/* Custom rendering of the analysis results */}
                        {(() => {
                          const responseText = results.response;
                          
                          // Split into main content and recommendation if present
                          const recommendationMatch = responseText.match(/\*\*Recommendation\*\*:|Recommendation:|\*\*Recommendations\*\*:|Recommendations:/i);
                          
                          let mainContent = responseText;
                          let recommendationContent = '';
                          
                          if (recommendationMatch) {
                            const splitIndex = recommendationMatch.index;
                            mainContent = responseText.substring(0, splitIndex);
                            recommendationContent = responseText.substring(splitIndex);
                            
                            // Clean up the recommendation content
                            recommendationContent = recommendationContent
                              .replace(/\*\*Recommendation\*\*:|Recommendation:|\*\*Recommendations\*\*:|Recommendations:/i, '')
                              .replace(/\*\*/g, '').trim();
                          }
                          
                          // Parse the main content into lines
                          const lines = mainContent.split('\n')
                            .map(line => line.trim())
                            .filter(line => {
                              // Filter out empty lines and lines that are just bullet points with no content
                              if (!line || line === '*' || line === '-' || line === '•') return false;
                              return true;
                            });
                          
                          // Process the recommendation content into lines
                          const recLines = recommendationContent ? 
                            recommendationContent.split('\n')
                              .map(line => line.trim())
                              .filter(line => {
                                // Filter out empty lines and lines that are just bullet points with no content
                                if (!line || line === '*' || line === '-' || line === '•') return false;
                                return true;
                              }) : [];
                          
                          return (
                            <>
                              {/* Main content */}
                              <List style={{ paddingLeft: '16px', listStyleType: 'none' }}>
                                {lines.map((line, index) => {
                                  // Remove any ** markers
                                  let cleanLine = line.replace(/\*\*/g, '');
                                  
                                  // Handle CO2 subscript
                                  cleanLine = cleanLine.replace(/CO2/g, 'CO₂');
                                  
                                  // Check if it's a heading (ends with a colon)
                                  const isHeading = line.endsWith(':');
                                  // Check if it contains a colon (category label)
                                  const colonIndex = line.indexOf(':');
                                  const hasCategory = colonIndex > 0 && !isHeading;
                                  
                                  // Check if it's a bullet point
                                  const isBullet = (line.startsWith('*') || line.startsWith('-')) && !isHeading;
                                  
                                  if (isHeading) {
                                    // Render as a heading
                                    return (
                                      <ListItem key={index} className={classes.headingLine}>
                                        <ListItemText 
                                          primary={cleanLine} 
                                          primaryTypographyProps={{ 
                                            variant: "subtitle1", 
                                            style: { fontWeight: 600 } 
                                          }} 
                                        />
                                      </ListItem>
                                    );
                                  } else if (hasCategory) {
                                    // Split into category and content
                                    const category = cleanLine.substring(0, colonIndex + 1);
                                    const content = cleanLine.substring(colonIndex + 1).trim();
                                    
                                    return (
                                      <ListItem key={index} className={classes.listBulletPoint}>
                                        <ListItemText 
                                          primary={
                                            <>
                                              <span style={{ fontWeight: 600 }}>{category}</span>
                                              {content}
                                            </>
                                          } 
                                        />
                                      </ListItem>
                                    );
                                  } else if (isBullet) {
                                    // Remove the bullet marker
                                    cleanLine = cleanLine.replace(/^[*-]\s+/, '');
                                    
                                    return (
                                      <ListItem key={index} className={classes.listBulletPoint}>
                                        <ListItemIcon className={classes.bulletIcon}>
                                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#D94936', marginTop: '8px', flexShrink: 0 }} />
                                        </ListItemIcon>
                                        <ListItemText primary={cleanLine} />
                                      </ListItem>
                                    );
                                  } else {
                                    return (
                                      <ListItem key={index} className={classes.textLine}>
                                        <ListItemText primary={cleanLine} />
                                      </ListItem>
                                    );
                                  }
                                })}
                              </List>
                              
                              {/* Recommendation section */}
                              {recommendationContent && (
                                <div className={classes.recommendationSection}>
                                  <Typography variant="subtitle1" className={classes.recommendationTitle}>
                                    Recommendation
                                  </Typography>
                                  <div className={classes.recommendationContent}>
                                    <List style={{ paddingLeft: '16px', listStyleType: 'none' }}>
                                      {recLines.map((line, index) => {
                                        // Remove any ** markers
                                        let cleanLine = line.replace(/\*\*/g, '');
                                        
                                        // Handle CO2 subscript
                                        cleanLine = cleanLine.replace(/CO2/g, 'CO₂');
                                        
                                        // Check if it's a heading (ends with a colon)
                                        const isHeading = line.endsWith(':');
                                        // Check if it contains a colon (category label)
                                        const colonIndex = line.indexOf(':');
                                        const hasCategory = colonIndex > 0 && !isHeading;
                                        
                                        // Check if it's a bullet point
                                        const isBullet = (line.startsWith('*') || line.startsWith('-')) && !isHeading;
                                        
                                        if (isHeading) {
                                          // Render as a heading
                                          return (
                                            <ListItem key={index} className={classes.headingLine}>
                                              <ListItemText 
                                                primary={cleanLine} 
                                                primaryTypographyProps={{ 
                                                  variant: "subtitle1", 
                                                  style: { fontWeight: 600, color: '#4caf50' } 
                                                }} 
                                              />
                                            </ListItem>
                                          );
                                        } else if (hasCategory) {
                                          // Split into category and content
                                          const category = cleanLine.substring(0, colonIndex + 1);
                                          const content = cleanLine.substring(colonIndex + 1).trim();
                                          
                                          return (
                                            <ListItem key={index} className={classes.listBulletPoint}>
                                              <ListItemIcon className={classes.bulletIcon}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4caf50', marginTop: '8px', flexShrink: 0 }} />
                                              </ListItemIcon>
                                              <ListItemText 
                                                primary={
                                                  <>
                                                    <span style={{ fontWeight: 600 }}>{category}</span>
                                                    {content}
                                                  </>
                                                } 
                                              />
                                            </ListItem>
                                          );
                                        } else if (isBullet) {
                                          // Remove the bullet marker
                                          cleanLine = cleanLine.replace(/^[*-]\s+/, '');
                                          
                                          return (
                                            <ListItem key={index} className={classes.listBulletPoint}>
                                              <ListItemIcon className={classes.bulletIcon}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4caf50', marginTop: '8px', flexShrink: 0 }} />
                                              </ListItemIcon>
                                              <ListItemText primary={cleanLine} />
                                            </ListItem>
                                          );
                                        } else {
                                          return (
                                            <ListItem key={index} className={classes.textLine}>
                                              <ListItemText primary={cleanLine} />
                                            </ListItem>
                                          );
                                        }
                                      })}
                                    </List>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </Paper>
                </div>
              </div>
            )}
            </div>
          </div>
        </CardContent>
        
        <CardFooter>
          <Typography variant="caption" color="textSecondary">
            Incident Intelligence is powered by AI analysis of your incident reports. Results may require human review and validation.
          </Typography>
        </CardFooter>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this query from your history?
            {itemToDelete && (
              <Typography 
                variant="body2" 
                style={{ 
                  marginTop: '16px', 
                  padding: '12px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px',
                  fontStyle: 'italic'
                }}
              >
                "{itemToDelete.query}"
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeDeleteDialog} color="primary">
            Cancel
          </MuiButton>
          <MuiButton onClick={handleDeleteHistoryItem} color="secondary" autoFocus>
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VectorSearch;
